import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectComboProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  icon?: React.ReactNode;
  className?: string;
}

export const MultiSelectCombo: React.FC<MultiSelectComboProps> = ({
  options,
  selectedValues = [],
  onChange,
  placeholder,
  icon,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search query
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opt.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allSelected = options.length > 0 && selectedValues.length === options.length;
  const noneSelected = selectedValues.length === 0;

  // Toggle single item selection
  const handleToggleOption = (val: string) => {
    if (selectedValues.includes(val)) {
      onChange(selectedValues.filter(v => v !== val));
    } else {
      onChange([...selectedValues, val]);
    }
  };

  // Toggle select all
  const handleToggleSelectAll = () => {
    if (allSelected) {
      // If all are selected, deselect all
      onChange([]);
    } else {
      // Otherwise, select all
      onChange(options.map(o => o.value));
    }
  };

  // Format button label based on selections
  const getButtonLabel = () => {
    if (noneSelected || allSelected) {
      return placeholder; // e.g. "All States" or similar
    }
    if (selectedValues.length === 1) {
      const match = options.find(o => o.value === selectedValues[0]);
      return match ? match.label : selectedValues[0];
    }
    if (selectedValues.length <= 2) {
      return selectedValues
        .map(val => {
          const match = options.find(o => o.value === val);
          return match ? match.value : val; // use short value if available
        })
        .join(', ');
    }
    return `${selectedValues.length} items`;
  };

  return (
    <div ref={containerRef} className={`relative inline-block text-left w-full ${className}`}>
      {/* Combo Box Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-1.5 px-3.5 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 focus:border-indigo-500 rounded-xl text-xs font-semibold text-gray-700 transition-all cursor-pointer shadow-2xs"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {icon && <span className="text-gray-400 shrink-0">{icon}</span>}
          <span className="truncate text-gray-700">{getButtonLabel()}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!noneSelected && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              className="p-0.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear all selections"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Combo Box Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1.5 w-full min-w-[240px] max-w-sm bg-white border border-gray-200 rounded-xl shadow-lg focus:outline-none overflow-hidden"
          >
            {/* Search Input inside Dropdown */}
            {options.length > 8 && (
              <div className="p-2 border-b border-gray-100 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-100 focus:bg-white focus:border-indigo-500 rounded-lg text-xs outline-none transition-all placeholder:text-gray-400"
                />
              </div>
            )}

            {/* Scrollable list */}
            <div className="max-h-[220px] overflow-y-auto py-1 divide-y divide-gray-50">
              {/* Select All Option */}
              <label className="flex items-center px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleToggleSelectAll}
                  className="w-3.5 h-3.5 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500 mr-2.5 cursor-pointer accent-indigo-600"
                />
                <span className="flex-1 select-none">
                  {allSelected ? 'Deselect All' : 'Select All'}
                </span>
                {allSelected && <Check className="w-3.5 h-3.5 text-indigo-600 ml-2 shrink-0" />}
              </label>

              {/* Individual Options */}
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-gray-400 font-sans">
                  No matches found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isChecked = selectedValues.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleOption(opt.value)}
                        className="w-3.5 h-3.5 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500 mr-2.5 cursor-pointer accent-indigo-600"
                      />
                      <span className="flex-1 select-none truncate" title={opt.label}>
                        {opt.label}
                      </span>
                      {isChecked && <Check className="w-3.5 h-3.5 text-indigo-600 ml-2 shrink-0" />}
                    </label>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
